import { apiRequest } from '@/domains/api';
import type {
  IReminderAudienceCount,
  IReminderPlan,
  ReminderStepInput
} from './cycle-reminder-contracts';

/**
 * survey-api's reminder plan of one survey pin (SR-523 stage 3, `cycles.manage`). A save quotes
 * the plan's revision (`"0"` for none yet); a stale one is 409 with the plan as it is now, and a
 * step survey-api refuses is 400 at `steps[i].<field>`.
 */

function remindersPath(cycleCode: string, surveyCode: string): string {
  return `/api/v1/admin/ranking-cycles/${encodeURIComponent(cycleCode)}/surveys/${encodeURIComponent(surveyCode)}/reminders`;
}

function planOf(plan: IReminderPlan): IReminderPlan {
  return { ...plan, steps: plan.steps ?? [] };
}

export const cycleRemindersApi = {
  async get(cycleCode: string, surveyCode: string, signal?: AbortSignal): Promise<IReminderPlan> {
    return planOf(await apiRequest<IReminderPlan>(remindersPath(cycleCode, surveyCode), { signal }));
  },

  async save(
    cycleCode: string,
    surveyCode: string,
    revision: number,
    steps: ReminderStepInput[]
  ): Promise<IReminderPlan> {
    return planOf(
      await apiRequest<IReminderPlan>(remindersPath(cycleCode, surveyCode), {
        method: 'PUT',
        body: { steps },
        headers: { 'If-Match': `"${revision}"` }
      })
    );
  },

  /** Who the step would reach if it fired now. */
  async audience(cycleCode: string, surveyCode: string, stepId: string, signal?: AbortSignal): Promise<IReminderAudienceCount> {
    const count = await apiRequest<IReminderAudienceCount>(
      `${remindersPath(cycleCode, surveyCode)}/${encodeURIComponent(stepId)}/audience`,
      { signal }
    );
    return { ...count, excluded: count.excluded ?? [] };
  },

  /** Fires an unfired step now; 409 once it has fired. */
  sendNow(cycleCode: string, surveyCode: string, stepId: string): Promise<{ campaignId: string }> {
    return apiRequest<{ campaignId: string }>(
      `${remindersPath(cycleCode, surveyCode)}/${encodeURIComponent(stepId)}/send-now`,
      { method: 'POST' }
    );
  }
};
